import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  CheckCircle,
  Download,
  FileText,
  Loader2,
  Save,
  Shield,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import type { Company, Nom35PolicyCommitments, Nom35PolicyConfig } from '@/types';
import { generatePolicyPdf } from '@/lib/policyPdfReport';

const DEFAULT_COMMITMENTS: Nom35PolicyCommitments = {
  prevencionFactoresRiesgo: true,
  ceroToleranciaViolencia: true,
  promocionEntornoOrganizacionalFavorable: true,
  mecanismosQuejaConfidenciales: true,
};

function todayISODate(): string {
  return new Date().toISOString().slice(0, 10);
}

function mergePolicyFromFirestore(
  raw: unknown,
  fallbackRazonSocial: string
): Omit<Nom35PolicyConfig, 'updatedAt'> {
  if (!raw || typeof raw !== 'object') {
    return {
      razonSocial: fallbackRazonSocial,
      centroTrabajo: '',
      representanteLegal: '',
      fechaEmision: todayISODate(),
      commitments: { ...DEFAULT_COMMITMENTS },
    };
  }
  const o = raw as Record<string, unknown>;
  const c = o.commitments as Record<string, unknown> | undefined;
  return {
    razonSocial: typeof o.razonSocial === 'string' ? o.razonSocial : fallbackRazonSocial,
    centroTrabajo: typeof o.centroTrabajo === 'string' ? o.centroTrabajo : '',
    representanteLegal:
      typeof o.representanteLegal === 'string' ? o.representanteLegal : '',
    fechaEmision:
      typeof o.fechaEmision === 'string' && o.fechaEmision.length >= 10
        ? o.fechaEmision.slice(0, 10)
        : todayISODate(),
    commitments: {
      prevencionFactoresRiesgo:
        typeof c?.prevencionFactoresRiesgo === 'boolean'
          ? c.prevencionFactoresRiesgo
          : DEFAULT_COMMITMENTS.prevencionFactoresRiesgo,
      ceroToleranciaViolencia:
        typeof c?.ceroToleranciaViolencia === 'boolean'
          ? c.ceroToleranciaViolencia
          : DEFAULT_COMMITMENTS.ceroToleranciaViolencia,
      promocionEntornoOrganizacionalFavorable:
        typeof c?.promocionEntornoOrganizacionalFavorable === 'boolean'
          ? c.promocionEntornoOrganizacionalFavorable
          : DEFAULT_COMMITMENTS.promocionEntornoOrganizacionalFavorable,
      mecanismosQuejaConfidenciales:
        typeof c?.mecanismosQuejaConfidenciales === 'boolean'
          ? c.mecanismosQuejaConfidenciales
          : DEFAULT_COMMITMENTS.mecanismosQuejaConfidenciales,
    },
  };
}

const COMMITMENT_ITEMS: {
  key: keyof Nom35PolicyCommitments;
  label: string;
  description: string;
}[] = [
  {
    key: 'prevencionFactoresRiesgo',
    label: 'Prevención de factores de riesgo psicosocial',
    description: 'Identificar y prevenir condiciones del entorno que puedan afectar la salud mental.',
  },
  {
    key: 'ceroToleranciaViolencia',
    label: 'Cero tolerancia a la violencia laboral',
    description: 'Política explícita de no tolerancia al acoso y hostigamiento.',
  },
  {
    key: 'promocionEntornoOrganizacionalFavorable',
    label: 'Entorno organizacional favorable',
    description: 'Promover condiciones que favorezcan el bienestar y el desarrollo del personal.',
  },
  {
    key: 'mecanismosQuejaConfidenciales',
    label: 'Mecanismos de queja confidenciales',
    description: 'Canales seguros y confidenciales para presentar quejas o denuncias.',
  },
];

export function SettingsPoliciesPage() {
  const { appUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasPersistedConfig, setHasPersistedConfig] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  const [companyCreatedAt, setCompanyCreatedAt] = useState<string | null>(null);
  const [razonSocial, setRazonSocial] = useState('');
  const [centroTrabajo, setCentroTrabajo] = useState('');
  const [representanteLegal, setRepresentanteLegal] = useState('');
  const [fechaEmision, setFechaEmision] = useState(todayISODate());
  const [commitments, setCommitments] = useState<Nom35PolicyCommitments>({
    ...DEFAULT_COMMITMENTS,
  });
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pdfNotice, setPdfNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!appUser?.companyId) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'companies', appUser.companyId!));
        if (cancelled) return;
        const data = snap.data() ?? {};
        const name =
          snap.exists() && typeof data.name === 'string'
            ? (data.name as string)
            : '';
        setCompanyName(name);
        setCompanyLogoUrl((data.branding?.logoUrl as string | undefined) ?? null);
        setCompanyCreatedAt(
          typeof data.createdAt === 'string' ? (data.createdAt as string) : null
        );
        const merged = mergePolicyFromFirestore(
          data.nom35PolicyConfig,
          name
        );
        setHasPersistedConfig(Boolean(data.nom35PolicyConfig));
        setRazonSocial(merged.razonSocial || companyName);
        setCentroTrabajo(merged.centroTrabajo);
        setRepresentanteLegal(merged.representanteLegal);
        setFechaEmision(merged.fechaEmision);
        setCommitments(merged.commitments);
      } catch (e) {
        console.error(e);
        if (!cancelled) setSaveError('No se pudieron cargar los datos de la empresa.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appUser?.companyId]);

  const setCommitment = useCallback((key: keyof Nom35PolicyCommitments, value: boolean) => {
    setCommitments((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSavePolicyConfig = async () => {
    if (!appUser?.companyId) return;
    setSaveSuccess(null);
    setSaveError(null);
    setIsSaving(true);
    const payload: Nom35PolicyConfig = {
      razonSocial: razonSocial.trim(),
      centroTrabajo: centroTrabajo.trim(),
      representanteLegal: representanteLegal.trim(),
      fechaEmision,
      commitments: { ...commitments },
      updatedAt: new Date().toISOString(),
    };
    try {
      await setDoc(
        doc(db, 'companies', appUser.companyId),
        {
          nom35PolicyConfig: payload,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      setSaveSuccess('Configuración de política guardada correctamente.');
      setHasPersistedConfig(true);
    } catch (err) {
      console.error(err);
      const msg =
        err instanceof Error ? err.message : 'Error al guardar. Intenta de nuevo.';
      setSaveError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!appUser?.companyId) return;
    setPdfNotice(null);
    setSaveError(null);
    if (!hasPersistedConfig) {
      setPdfNotice('Primero guarda la configuración de la política antes de generar el PDF.');
      return;
    }

    const company: Company = {
      id: appUser.companyId,
      name: razonSocial || companyName || 'Empresa',
      industry: undefined,
      country: undefined,
      size: undefined,
      branding: {
        primaryColor: undefined,
        logoUrl: companyLogoUrl ?? undefined,
      },
      createdAt: companyCreatedAt ?? new Date().toISOString(),
      nom35PolicyConfig: {
        razonSocial: razonSocial.trim() || companyName,
        centroTrabajo: centroTrabajo.trim(),
        representanteLegal: representanteLegal.trim(),
        fechaEmision,
        commitments: { ...commitments },
        updatedAt: new Date().toISOString(),
      },
    };

    try {
      await generatePolicyPdf(company);
    } catch (err) {
      console.error(err);
      const msg =
        err instanceof Error ? err.message : 'No se pudo generar el documento PDF.';
      setPdfNotice(msg);
    }
  };

  if (!appUser?.companyId) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/app/settings">← Ajustes</Link>
        </Button>
        <p className="text-muted-foreground">No tienes empresa asignada.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        <span>Cargando configuración…</span>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 pb-10">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/app/settings">← Volver a Ajustes</Link>
      </Button>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Shield className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Política NOM-035 (Riesgos psicosociales)
            </h1>
            <p className="text-sm text-muted-foreground">
              Generador según Guía de referencia IV. Completa los datos y guarda la configuración
              antes de exportar el documento oficial.
            </p>
          </div>
        </div>
      </div>

      {saveSuccess && (
        <div
          className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-900 dark:bg-green-950/40 dark:text-green-200"
          role="status"
        >
          <CheckCircle className="h-4 w-4 shrink-0" aria-hidden />
          {saveSuccess}
        </div>
      )}
      {saveError && (
        <div
          className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          role="alert"
        >
          {saveError}
        </div>
      )}
      {pdfNotice && (
        <div
          className="flex items-center gap-2 rounded-lg border bg-muted/50 px-4 py-3 text-sm"
          role="status"
        >
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          {pdfNotice}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-muted-foreground" aria-hidden />
            Datos de la empresa y de la política
          </CardTitle>
          <CardDescription>
            Estos campos se usarán en el cuerpo del documento de política de prevención.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="razonSocial">Razón social</Label>
            <Input
              id="razonSocial"
              value={razonSocial}
              onChange={(e) => setRazonSocial(e.target.value)}
              placeholder="Ej. Mi Empresa, S.A. de C.V."
              autoComplete="organization"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="centroTrabajo">Centro de trabajo</Label>
            <Input
              id="centroTrabajo"
              value={centroTrabajo}
              onChange={(e) => setCentroTrabajo(e.target.value)}
              placeholder="Nombre o ubicación del centro"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="representanteLegal">Representante legal (firma / autorización)</Label>
            <Input
              id="representanteLegal"
              value={representanteLegal}
              onChange={(e) => setRepresentanteLegal(e.target.value)}
              placeholder="Nombre completo"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fechaEmision">Fecha de emisión de la política</Label>
            <Input
              id="fechaEmision"
              type="date"
              value={fechaEmision}
              onChange={(e) => setFechaEmision(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Compromisos en la política (NOM-035)</CardTitle>
          <CardDescription>
            Marca los apartados que debe incluir tu política. Alineados a obligaciones de la norma.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {COMMITMENT_ITEMS.map((item) => (
            <div
              key={item.key}
              className="flex gap-3 rounded-lg border bg-card p-4 transition-colors hover:bg-muted/30"
            >
              <Input
                id={`commit-${item.key}`}
                type="checkbox"
                checked={commitments[item.key]}
                onChange={(e) => setCommitment(item.key, e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-input"
              />
              <div className="min-w-0 flex-1">
                <Label
                  htmlFor={`commit-${item.key}`}
                  className="cursor-pointer text-sm font-medium leading-snug"
                >
                  {item.label}
                </Label>
                <p className="mt-1 text-xs text-muted-foreground">{item.description}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <Button
          type="button"
          variant="outline"
          disabled={isSaving}
          onClick={() => void handleSavePolicyConfig()}
          className="gap-2"
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Save className="h-4 w-4" aria-hidden />
          )}
          Guardar Configuración de Política
        </Button>
        <Button
          type="button"
          onClick={() => void handleGeneratePDF()}
          disabled={!hasPersistedConfig}
          className="gap-2"
        >
          <Download className="h-4 w-4" aria-hidden />
          Generar Documento PDF
        </Button>
      </div>
    </div>
  );
}
